/**
 * Pure logic behind the Heatmaps page (Paper "03 - App · Heatmaps & Funnels",
 * "Heatmaps · Simple · …"): the answer sentence, the top hotspots, the scroll
 * reach lines and the "collecting clicks" threshold. No React, no fetch, so it
 * is unit tested in `src/__tests__/components/heatmaps/heatmapInsights.test.ts`.
 */
import { addDays, differenceInCalendarDays, format, startOfDay, startOfMonth, subDays, subMonths, subYears } from 'date-fns'
import type { ScrollDepthSummary } from '@/lib/utils/heatmapUtils'

/**
 * Clicks a page needs before its heatmap is readable (approved by the founder,
 * 2026-10). Below this the page shows each click as a dot instead of heat, and
 * the answer sentence says the map is still collecting.
 */
export const HEATMAP_READABLE_CLICKS = 100

export const isHeatmapReadable = (totalClicks: number) => totalClicks >= HEATMAP_READABLE_CLICKS

export type HeatmapMode = 'clicks' | 'scroll'
export type HeatmapDevice = 'desktop' | 'mobile'

export type HeatmapPointLike = {
  x: number
  y: number
  value: number
  count?: number
  target?: string | null
  secondary?: string | null
}

export type HeatmapElement = {
  /** Stable key (the target, or a position bucket for unlabelled clicks) */
  key: string
  target: string | null
  secondary: string | null
  clicks: number
  /** Share of all clicks on the page, 0 to 100 */
  share: number
  /** 1-based rank by clicks */
  rank: number
  /** Click-weighted centre, in % of the capture */
  x: number
  y: number
  /** Extent of the element's clicks, in % of the capture */
  minX: number
  maxX: number
  minY: number
  maxY: number
}

const pointClicks = (p: HeatmapPointLike) => p.count || p.value || 1

export const totalClicksOf = (points: HeatmapPointLike[]) =>
  points.reduce((sum, p) => sum + pointClicks(p), 0)

/**
 * Group click points into elements (by target), ranked by clicks. Unlabelled
 * points are grouped by a coarse position bucket so a cluster of clicks on an
 * element the tracker could not name still reads as one hotspot.
 */
/**
 * Which element a click belongs to. A generic component name ("Link",
 * "button") is shared by every link on the page, so it groups by what the
 * visitor saw (text or href) instead; else one "Link" row swallows 100%.
 */
export function heatmapElementKey(p: HeatmapPointLike): string {
  if (!p.target) return `p:${Math.round(p.x / 5)}:${Math.round(p.y / 5)}`
  return isGenericTarget(p.target) && p.secondary ? `t:${p.target}|${p.secondary}` : `t:${p.target}`
}

export function rankHeatmapElements(points: HeatmapPointLike[]): HeatmapElement[] {
  const total = totalClicksOf(points)
  const groups = new Map<string, { target: string | null; secondary: string | null; pts: HeatmapPointLike[] }>()

  for (const p of points) {
    const key = heatmapElementKey(p)
    const g = groups.get(key) ?? { target: p.target ?? null, secondary: p.secondary ?? null, pts: [] }
    if (!g.secondary && p.secondary) g.secondary = p.secondary
    g.pts.push(p)
    groups.set(key, g)
  }

  return Array.from(groups.entries())
    .map(([key, g]) => {
      const clicks = totalClicksOf(g.pts)
      return {
        key,
        target: g.target,
        secondary: g.secondary,
        clicks,
        share: total > 0 ? (clicks / total) * 100 : 0,
        rank: 0,
        x: g.pts.reduce((s, p) => s + p.x * pointClicks(p), 0) / clicks,
        y: g.pts.reduce((s, p) => s + p.y * pointClicks(p), 0) / clicks,
        minX: Math.min(...g.pts.map((p) => p.x)),
        maxX: Math.max(...g.pts.map((p) => p.x)),
        minY: Math.min(...g.pts.map((p) => p.y)),
        maxY: Math.max(...g.pts.map((p) => p.y)),
      }
    })
    .sort((a, b) => b.clicks - a.clicks)
    .map((el, i) => ({ ...el, rank: i + 1 }))
}

/** Path shown in the page selector and the answer sentence ("/pricing"). */
export function pagePath(pageUrl: string): string {
  try {
    const u = new URL(pageUrl)
    return u.pathname + u.search || '/'
  } catch {
    return pageUrl.startsWith('/') ? pageUrl : `/${pageUrl}`
  }
}

export const formatCount = (n: number) => Math.round(n).toLocaleString('en-US')

const clickNoun = (device: HeatmapDevice, n = 2) =>
  device === 'mobile' ? (n === 1 ? 'tap' : 'taps') : n === 1 ? 'click' : 'clicks'

const GENERIC_TARGETS = new Set(['link', 'a', 'button', 'div', 'span', 'img', 'svg', 'li', 'p', 'nav', 'section'])

/** True for tag or component names ("Link", "button") that name no particular element. */
export const isGenericTarget = (target: string | null | undefined) =>
  !!target && GENERIC_TARGETS.has(target.trim().toLowerCase())

/** "/work-with-me" from an absolute URL on any host; text stays text. */
function readableSecondary(secondary: string): string {
  const s = secondary.trim()
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s)
      return `${u.hostname.replace(/^www\./, '')}${u.pathname === '/' ? '' : u.pathname}`
    } catch {
      return s
    }
  }
  return s.length > 48 ? `${s.slice(0, 47)}…` : s
}

/** Text or a link a person can read: a letter or digit, or a path / URL ("/" is the homepage; "→" alone is not). */
const hasWords = (v: string | null | undefined) => !!v && (/^(\/|https?:\/\/)/i.test(v.trim()) || /[\p{L}\p{N}]/u.test(v))

/**
 * What a person would call the element: its text or link for a generic
 * target, else its name. A generic target with no readable text (an icon or
 * arrow-only link) is "Unlabelled link", not the component name "Link".
 */
export function elementName(el: Pick<HeatmapElement, 'target' | 'secondary'>): string | null {
  if (el.target && isGenericTarget(el.target)) {
    if (hasWords(el.secondary)) return readableSecondary(el.secondary!)
    return `Unlabelled ${el.target.trim().toLowerCase() === 'a' ? 'link' : el.target.trim().toLowerCase()}`
  }
  return el.target ?? (el.secondary ? readableSecondary(el.secondary) : null)
}

/** "Clicked “Book a demo”" / "Clicked an unlabelled link" for live feeds; one rule with heatmaps. */
export function clickedPhrase(target?: string | null, secondary?: string | null): string {
  if (!target && !secondary) return 'Clicked'
  return `Clicked ${elementLabel({ target: target ?? null, secondary: secondary ?? null })}`
}

export const elementLabel = (el: Pick<HeatmapElement, 'target' | 'secondary'>) => {
  const name = elementName(el)
  if (!name) return 'an unlabelled element'
  return name.startsWith('Unlabelled ') ? `an ${name.charAt(0).toLowerCase()}${name.slice(1)}` : `“${name}”`
}

/** Periods the heatmap endpoint answers (custom ranges are not supported). */
export const HEATMAP_PERIODS = [
  { value: '1d', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'mtd', label: 'Month to date' },
  { value: '12m', label: 'Last 12 months' },
] as const

/** Resolve a preset period into the dates it covers (local time). */
export function resolvePeriod(period: string, now = new Date()): { start: Date; end: Date } {
  const rolling = period.match(/^(\d+)d$/)
  if (rolling) {
    const days = Number(rolling[1])
    // "Last 7 days" is today plus the six before it, as in DateDropdown
    return { start: days <= 1 ? subDays(now, 1) : startOfDay(subDays(now, days - 1)), end: now }
  }
  if (period === 'mtd') return { start: startOfMonth(now), end: now }
  if (period === '12m') return { start: subMonths(now, 12), end: now }
  if (period === 'ytd') return { start: new Date(now.getFullYear(), 0, 1), end: now }
  if (period === 'ALL_TIME') return { start: subYears(now, 10), end: now }
  return { start: subDays(now, 30), end: now }
}

/** "Sep 19 to Sep 25" — the range label used by the date control and sublines. */
export function formatPeriodRange(period: string, now = new Date()): string {
  const { start, end } = resolvePeriod(period, now)
  const sameYear = start.getFullYear() === end.getFullYear() && end.getFullYear() === now.getFullYear()
  const f = sameYear ? 'MMM d' : 'MMM d, yyyy'
  return `${format(start, f)} to ${format(end, f)}`
}

export type HeatmapAnswer = { title: string; subline: string }

/** Clicks mode: "26% of clicks on /pricing hit “Book a demo”". */
export function buildClicksAnswer(args: {
  path: string
  device: HeatmapDevice
  top: HeatmapElement | undefined
  totalClicks: number
  visitors: number | null
  rangeLabel: string
}): HeatmapAnswer {
  const { path, device, top, totalClicks, visitors, rangeLabel } = args
  const noun = clickNoun(device)
  const deviceNoun = device === 'mobile' ? `mobile ${noun}` : noun
  const title = top
    ? `${Math.round(top.share)}% of ${deviceNoun} on ${path} hit ${elementLabel(top)}`
    : `No ${deviceNoun} on ${path} yet`
  // Visitors are page-wide (all devices), so they only ride along on desktop,
  // where Paper shows them; a mobile figure would need a device split.
  const from = visitors !== null && device === 'desktop' ? ` from ${formatCount(visitors)} visitors` : ''
  return {
    title,
    subline: `${formatCount(totalClicks)} ${clickNoun(device, totalClicks)}${from}, ${rangeLabel}`,
  }
}

/**
 * Reach (share of visitors, 0 to 100) at a scroll depth (0 to 100, % of page
 * height), interpolated between bucket starts.
 */
export function reachAtDepth(scroll: ScrollDepthSummary, depth: number): number | null {
  if (!scroll || scroll.totalSessions === 0 || scroll.buckets.length === 0) return null
  const pts = [...scroll.buckets]
    .sort((a, b) => a.startPercent - b.startPercent)
    .map((b) => ({ d: b.startPercent, r: b.reachPercentage }))
  if (depth <= pts[0].d) return pts[0].r
  for (let i = 1; i < pts.length; i++) {
    if (depth <= pts[i].d) {
      const a = pts[i - 1]
      const b = pts[i]
      return a.r + ((b.r - a.r) * (depth - a.d)) / (b.d - a.d || 1)
    }
  }
  return pts[pts.length - 1].r
}

/** Scroll depth (% of page height) where reach first falls to `reach`. Null when it never does. */
export function depthAtReach(scroll: ScrollDepthSummary, reach: number): number | null {
  if (!scroll || scroll.totalSessions === 0 || scroll.buckets.length === 0) return null
  const pts = [...scroll.buckets]
    .sort((a, b) => a.startPercent - b.startPercent)
    .map((b) => ({ d: b.startPercent, r: b.reachPercentage }))
  if (pts[0].r <= reach) return pts[0].d
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]
    const b = pts[i]
    if (b.r <= reach) {
      const t = (a.r - reach) / (a.r - b.r || 1)
      return a.d + (b.d - a.d) * t
    }
  }
  return null
}

/** The three reach lines drawn on the scroll map. 50% is the average fold. */
export const SCROLL_REACH_LINES = [75, 50, 25] as const

/** Scroll mode: "1 in 3 visitors reach the end of /pricing". */
export function buildScrollAnswer(args: {
  path: string
  scroll: ScrollDepthSummary
  rangeLabel: string
}): HeatmapAnswer {
  const { path, scroll, rangeLabel } = args
  if (!scroll || scroll.totalSessions === 0) {
    return { title: `No scroll data for ${path} yet`, subline: rangeLabel }
  }
  const endReach = reachAtDepth(scroll, 100) ?? 0
  let title: string
  if (endReach < 1) title = `Almost no visitors reach the end of ${path}`
  else if (endReach < 50) title = `1 in ${Math.round(100 / endReach)} visitors reach the end of ${path}`
  else title = `${Math.round(endReach)}% of visitors reach the end of ${path}`
  return {
    title,
    subline: `${Math.round(scroll.averageScroll)}% average scroll depth across ${formatCount(scroll.totalSessions)} visitors, ${rangeLabel}`,
  }
}

/** One line under the selected element's stats ("Clicked 2x more than any other element. …"). */
export function elementInsight(el: HeatmapElement, ranked: HeatmapElement[], device: HeatmapDevice): string {
  const noun = device === 'mobile' ? 'Tapped' : 'Clicked'
  if (el.rank === 1 && ranked.length > 1) {
    const ratio = el.clicks / Math.max(1, ranked[1].clicks)
    if (ratio >= 1.5) {
      return `${noun} ${Math.round(ratio * 10) / 10}x more than any other element. Worth testing copy or placement.`
    }
    return `${noun} more than any other element. Worth testing copy or placement.`
  }
  if (el.rank <= 3) return `One of the three most ${noun.toLowerCase()} elements on this page.`
  return `${Math.round(el.share)}% of ${clickNoun(device)} on this page land here.`
}

/** Collecting state copy: pace and the date the map should become readable. */
export function collectingPace(args: {
  totalClicks: number
  period: string
  device: HeatmapDevice
  now?: Date
}): string {
  const { totalClicks, period, device, now = new Date() } = args
  const { start, end } = resolvePeriod(period, now)
  const days = Math.max(1, differenceInCalendarDays(end, start) + 1)
  const perDay = totalClicks / days
  const noun = clickNoun(device)
  if (perDay < 0.5) return `Fewer than one ${clickNoun(device, 1)} a day so far.`
  const remaining = Math.max(0, HEATMAP_READABLE_CLICKS - totalClicks)
  const eta = addDays(now, Math.ceil(remaining / perDay))
  return `About ${Math.round(perDay)} ${noun} a day. At this pace the map is readable around ${format(eta, 'MMM d')}.`
}

export function buildCollectingAnswer(args: {
  path: string
  device: HeatmapDevice
  totalClicks: number
  visitors: number | null
  rangeLabel: string
}): HeatmapAnswer {
  const { path, device, totalClicks, visitors, rangeLabel } = args
  const noun = clickNoun(device)
  const from = visitors !== null && device === 'desktop' ? ` from ${formatCount(visitors)} visitors` : ''
  return {
    title: `Too few ${noun} on ${path} to read yet`,
    subline: `${formatCount(totalClicks)} ${clickNoun(device, totalClicks)}${from}, ${rangeLabel}. A heatmap gets readable at about ${HEATMAP_READABLE_CLICKS}.`,
  }
}

/** Deep link into experiment creation for a page (and the element, when known). */
export function experimentLink(basePath: string, pageUrl: string, element?: string | null): string {
  const params = new URLSearchParams({ page: pageUrl })
  if (element) params.set('element', element)
  return `${basePath}/split-testing/new?${params.toString()}`
}

/**
 * Which device a point came from. The API tags each point with the
 * screen-size bucket it was recorded in; anything else is neither.
 */
export function deviceOfPoint(point: { screen_size_range?: string | null }): HeatmapDevice | null {
  const range = point.screen_size_range ?? ''
  if (range.includes('mobile') || range.includes('320') || range.includes('400')) return 'mobile'
  if (range.includes('desktop') || range.includes('1820') || range.includes('2020')) return 'desktop'
  return null
}

export type DeviceShares = { mobile: number; desktop: number; total: number }

/** Clicks per device on this page, for the device toggle and the default device. */
export function deviceClickShares(
  points: Array<HeatmapPointLike & { screen_size_range?: string | null }>,
): DeviceShares {
  let mobile = 0
  let desktop = 0
  for (const p of points) {
    const d = deviceOfPoint(p)
    if (d === 'mobile') mobile += pointClicks(p)
    else if (d === 'desktop') desktop += pointClicks(p)
  }
  return { mobile, desktop, total: mobile + desktop }
}

/**
 * The device the page opens on: whichever carries more clicks. A mostly-mobile
 * site (Meta ad traffic) opened on Desktop showed almost nothing (beta
 * feedback, 2026-10-23). Null with no clicks, so the caller keeps its default.
 */
export function dominantDevice(shares: DeviceShares): HeatmapDevice | null {
  if (shares.total === 0) return null
  return shares.mobile > shares.desktop ? 'mobile' : 'desktop'
}

/** "98%" for the toggle; "<1%" rather than a misleading 0. */
export function formatDeviceShare(clicks: number, total: number): string | null {
  if (total === 0) return null
  const pct = (clicks / total) * 100
  if (clicks > 0 && pct < 1) return '<1%'
  return `${Math.round(pct)}%`
}

/**
 * Viewport the fold is measured against, in CSS px: a common phone
 * (390 × 844) and a 1440 × 900 laptop. The capture is taken at the same width.
 */
export const FOLD_VIEWPORT: Record<HeatmapDevice, { width: number; height: number }> = {
  mobile: { width: 390, height: 844 },
  desktop: { width: 1440, height: 900 },
}

/**
 * Where the first screen ends, as % of page height. The capture's aspect ratio
 * gives the page height at the capture width, whatever pixel density the
 * screenshot was saved at. Null when the page fits on one screen.
 */
export function foldDepthPercent(args: {
  device: HeatmapDevice
  naturalWidth: number
  naturalHeight: number
  /** Capture width in CSS px, when the API reports it. */
  captureWidth?: number | null
}): number | null {
  const { device, naturalWidth, naturalHeight, captureWidth } = args
  if (!naturalWidth || !naturalHeight) return null
  const cssWidth = captureWidth || FOLD_VIEWPORT[device].width
  const pageHeight = naturalHeight * (cssWidth / naturalWidth)
  const depth = (FOLD_VIEWPORT[device].height / pageHeight) * 100
  return depth >= 100 ? null : depth
}

export type HeatmapStat = { label: string; value: string; hint: string }

/**
 * The numbers under the answer, Clarity style: volume, the top element's
 * share, and how far visitors get. Only measured values; a stat with no data
 * is left out rather than shown as 0.
 */
export function buildHeatmapStats(args: {
  device: HeatmapDevice
  elements: HeatmapElement[]
  totalClicks: number
  scroll: ScrollDepthSummary
  foldDepth: number | null
}): HeatmapStat[] {
  const { device, elements, totalClicks, scroll, foldDepth } = args
  const noun = clickNoun(device)
  const stats: HeatmapStat[] = [
    {
      label: device === 'mobile' ? 'Mobile taps' : 'Desktop clicks',
      value: formatCount(totalClicks),
      hint: elements.length ? `on ${formatCount(elements.length)} elements` : 'none yet',
    },
  ]
  const top = elements[0]
  if (top) {
    stats.push({
      label: 'Top element',
      value: `${Math.round(top.share)}%`,
      hint: `of ${noun} · ${elementName(top) ?? 'unlabelled element'}`,
    })
  }
  if (scroll && scroll.totalSessions > 0) {
    if (foldDepth !== null) {
      const past = reachAtDepth(scroll, Math.min(100, foldDepth + 1))
      if (past !== null) {
        stats.push({ label: 'Scroll past first screen', value: `${Math.round(past)}%`, hint: 'of visitors, all devices' })
      }
    }
    const end = reachAtDepth(scroll, 100)
    if (end !== null) {
      stats.push({ label: 'Reach the end', value: `${Math.round(end)}%`, hint: `${Math.round(scroll.averageScroll)}% average depth` })
    }
  }
  return stats
}
